import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const RejectedEmailLogs = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [skip, setSkip] = useState(0);
  const [reviewedFilter, setReviewedFilter] = useState('all'); // 'all', 'reviewed', 'unreviewed'
  const [viewingOriginal, setViewingOriginal] = useState(null);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
      });

      if (reviewedFilter === 'reviewed') {
        params.append('reviewed', 'true');
      } else if (reviewedFilter === 'unreviewed') {
        params.append('reviewed', 'false');
      }

      const response = await axios.get(
        `${apiBase}/admin/rejected-emails?${params}`,
        { headers }
      );

      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching rejected emails:', error);
    } finally {
      setLoading(false);
    }
  }, [skip, limit, reviewedFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/rejected-emails/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching rejected email stats:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchLogs();
      fetchStats();
    }
  }, [user, fetchLogs, fetchStats]);

  const toggleLogExpansion = (logId) => {
    setExpandedLogId(expandedLogId === logId ? null : logId);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const handleMarkReviewed = async (logId, reviewed) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.patch(
        `${apiBase}/admin/rejected-emails/${logId}/review`,
        { reviewed },
        { headers }
      );
      fetchLogs();
      fetchStats();
    } catch (error) {
      console.error('Error marking email as reviewed:', error);
    }
  };

  const handleViewOriginal = (logId) => {
    const apiBase = getApiBase();
    // Open in new window
    window.open(`${apiBase}/admin/rejected-emails/${logId}/original`, '_blank');
  };

  const getReasonColor = (reason) => {
    switch (reason) {
      case 'unauthorized_sender':
        return 'bg-red-100 text-red-800';
      case 'unknown_sender':
        return 'bg-yellow-100 text-yellow-800';
      case 'spam_suspected':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatReason = (reason) => {
    const labels = {
      'unauthorized_sender': 'Unauthorized',
      'unknown_sender': 'Unknown Sender',
      'spam_suspected': 'Spam',
      'invalid_format': 'Invalid Format',
      'other': 'Other'
    };
    return labels[reason] || reason;
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="p-6">
        <div className="text-center text-red-600">
          Access denied. Superadmin privileges required.
        </div>
      </div>
    );
  }

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rejected Emails</h1>
          <p className="mt-1 text-sm text-gray-500">
            Emails rejected due to unauthorized senders or other issues
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Filter */}
          <select
            value={reviewedFilter}
            onChange={(e) => {
              setReviewedFilter(e.target.value);
              setSkip(0);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Emails</option>
            <option value="unreviewed">Unreviewed</option>
            <option value="reviewed">Reviewed</option>
          </select>

          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setSkip(0);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={25}>Show 25</option>
            <option value={50}>Show 50</option>
            <option value={100}>Show 100</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Rejected</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Unreviewed</div>
            <div className="text-2xl font-bold text-red-600">{stats.unreviewed}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Reviewed</div>
            <div className="text-2xl font-bold text-green-600">{stats.reviewed}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Last 24 Hours</div>
            <div className="text-2xl font-bold text-blue-600">{stats.last_24h}</div>
          </div>
        </div>
      )}

      {/* Stats by Reason */}
      {stats?.by_reason && Object.keys(stats.by_reason).length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Rejection Reasons</h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(stats.by_reason).map(([reason, count]) => (
              <div key={reason} className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${getReasonColor(reason)}`}>
                  {formatReason(reason)}
                </span>
                <span className="text-sm text-gray-600">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination Info */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold">{logs.length}</span> of{' '}
            <span className="font-semibold">{total}</span> rejected emails
          </div>
          {total > limit && (
            <div className="flex gap-2">
              <button
                onClick={() => setSkip(Math.max(0, skip - limit))}
                disabled={skip === 0}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setSkip(skip + limit)}
                disabled={skip + limit >= total}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="text-4xl mb-2">📧</div>
            <p className="text-lg">No rejected emails</p>
            <p className="text-sm mt-2">Emails rejected at the inbox will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {logs.map((log) => (
              <div key={log.id} className={`p-4 hover:bg-gray-50 ${!log.reviewed ? 'bg-red-50' : ''}`}>
                <div
                  className="cursor-pointer"
                  onClick={() => toggleLogExpansion(log.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getReasonColor(log.rejection_reason)}`}>
                          {formatReason(log.rejection_reason)}
                        </span>
                        {log.reviewed ? (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                            Reviewed
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            Unreviewed
                          </span>
                        )}
                        {log.attachment_info && log.attachment_info.length > 0 && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            📎 {log.attachment_info.length}
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        {log.subject || '(No Subject)'}
                      </div>
                      <div className="text-sm text-gray-600 mb-1">
                        From: <span className="font-mono">{log.from_email}</span>
                        {log.from_name && <span className="text-gray-400"> ({log.from_name})</span>}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>{formatDate(log.received_at)}</span>
                        <span>To: {log.to_email}</span>
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <svg
                        className={`h-5 w-5 text-gray-400 transform transition-transform ${
                          expandedLogId === log.id ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {expandedLogId === log.id && (
                  <div className="mt-4 border-t border-gray-200 pt-4">
                    {/* Actions */}
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={() => handleViewOriginal(log.id)}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        View Original Email
                      </button>
                      {!log.reviewed ? (
                        <button
                          onClick={() => handleMarkReviewed(log.id, true)}
                          className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Mark as Reviewed
                        </button>
                      ) : (
                        <button
                          onClick={() => handleMarkReviewed(log.id, false)}
                          className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                        >
                          Mark as Unreviewed
                        </button>
                      )}
                    </div>

                    {/* Rejection Details */}
                    <div className="bg-red-50 p-3 rounded mb-4">
                      <div className="text-sm font-medium text-red-800 mb-1">Rejection Reason</div>
                      <div className="text-sm text-red-700">{log.rejection_details || log.rejection_reason}</div>
                    </div>

                    {/* Message Preview */}
                    {log.raw_email_data?.text && (
                      <div className="mb-4">
                        <div className="text-sm font-medium text-gray-700 mb-1">Message Preview</div>
                        <div className="bg-gray-50 p-3 rounded text-sm text-gray-600 max-h-40 overflow-y-auto whitespace-pre-wrap">
                          {log.raw_email_data.text.substring(0, 500)}
                          {log.raw_email_data.text.length > 500 && '...'}
                        </div>
                      </div>
                    )}

                    {/* Attachments */}
                    {log.attachment_info && log.attachment_info.length > 0 && (
                      <div className="mb-4">
                        <div className="text-sm font-medium text-gray-700 mb-2">Attachments</div>
                        <div className="bg-gray-50 p-3 rounded">
                          <ul className="text-sm text-gray-600 space-y-1">
                            {log.attachment_info.map((att, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <span>📎</span>
                                <span>{att.filename}</span>
                                <span className="text-gray-400">
                                  ({att.content_type}, {(att.size / 1024).toFixed(1)} KB)
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* Headers */}
                    {log.headers && Object.keys(log.headers).length > 0 && (
                      <div className="mb-4">
                        <div className="text-sm font-medium text-gray-700 mb-2">Email Headers</div>
                        <div className="bg-gray-50 p-3 rounded max-h-40 overflow-y-auto">
                          <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                            {Object.entries(log.headers).map(([key, value]) => (
                              <div key={key}><strong>{key}:</strong> {value}</div>
                            ))}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                      <div>
                        <span className="font-medium">Log ID:</span> {log.id}
                      </div>
                      <div>
                        <span className="font-medium">Message-ID:</span> {log.message_id || 'N/A'}
                      </div>
                      {log.reviewed_by && (
                        <>
                          <div>
                            <span className="font-medium">Reviewed by:</span> {log.reviewed_by}
                          </div>
                          <div>
                            <span className="font-medium">Reviewed at:</span> {formatDate(log.reviewed_at)}
                          </div>
                        </>
                      )}
                      {log.review_notes && (
                        <div className="col-span-2">
                          <span className="font-medium">Notes:</span> {log.review_notes}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RejectedEmailLogs;
